import type { PathSort } from "@sergio9929/pb-query";
import { type ClassValue, clsx } from "clsx";
import { setDefaultOptions } from "date-fns";
import type { DefaultNamespace, i18n, Resources } from "i18next";
import { parseAsArrayOf, parseAsJson } from "nuqs";
import { type FlagProps, isValidPhoneNumber } from "react-phone-number-input";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { getDateFnsLocale } from "@/lib/dates";
import { pb } from "@/lib/pocketbase";
import { getZodLocale } from "@/lib/zod";

export const UNLIMITED = -1;

export function cn(...inputs: Array<ClassValue>) {
	return twMerge(clsx(inputs));
}

export const getLocaleCountry = (locale: i18n["language"]): FlagProps => {
	switch (locale) {
		case "es":
			return { country: "ES", countryName: "Spanish" };
		case "en":
			return { country: "US", countryName: "English" };
		default:
			return { country: "US", countryName: "English" };
	}
};

export const debounce = <T extends (...args: Array<unknown>) => unknown>(fn: T, delay: number): T => {
	let timeoutId: ReturnType<typeof setTimeout>;
	return ((...args: Parameters<T>) => {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => fn(...args), delay);
	}) as T;
};

export const getInitials = (name: string): string =>
	name
		.split(" ")
		.map((part) => part[0])
		.join("")
		.toUpperCase()
		.slice(0, 2);

export function isEmpty(value: unknown): value is null | undefined | "" | [] | Record<string, never> {
	// null or undefined
	if (value === null || value === undefined) {
		return true;
	}

	// empty string
	if (typeof value === "string") {
		return value.trim() === "";
	}

	// empty array
	if (Array.isArray(value)) {
		return value.length === 0;
	}

	// empty object
	if (typeof value === "object") {
		return Object.keys(value).length === 0;
	}

	return false;
}

export const parseDatatableSort = (arr: Array<{ id: string; desc?: boolean }>) => {
	return (
		arr
			.map((item) => {
				const prefix = item.desc ? "-" : "";
				return `${prefix}${item.id}`;
			})
			// biome-ignore lint/style/noMagicNumbers: library
			.join(",") as unknown as Array<PathSort<unknown, 0>>
	);
};

const sortSchema = z.object({
	id: z.string(),
	desc: z.boolean().optional(),
});

export const parseAsSort = parseAsArrayOf(parseAsJson((raw) => sortSchema.parse(raw)))
	.withDefault([{ id: "created", desc: true }])
	.withOptions({ clearOnDefault: true });

export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

type DeepKeyOf<T> = T extends object
	? {
			[K in keyof T]: `${Exclude<K, symbol>}` | `${Exclude<K, symbol>}.${DeepKeyOf<T[K]>}`;
		}[keyof T]
	: never;

export type ValidT<NS extends keyof Resources = DefaultNamespace> = DeepKeyOf<Resources[NS]>;

export const HOURS = Array.from({ length: 24 }, (_, i) => i);

//TODO: Add i18n
export const fileSchema = ({
	maxSize = 5_242_880, // 5MB
}: {
	maxSize?: number;
} = {}) => {
	return z.union([z.string().min(1), z.file().max(maxSize)]);
};

const PASSWORD_LENGTH = 8;

export const passwordSchema = z
	.string()
	.min(PASSWORD_LENGTH)
	.regex(/[A-Z]/)
	.regex(/[a-z]/)
	.regex(/[0-9]/)
	.regex(/[!@#$%^&*(),.?":{}|<>]/);

export const phoneNumberSchema = () => {
	return z.string().refine(isValidPhoneNumber);
};

export async function safePromise<T>(promise: Promise<T>): Promise<[undefined, T] | [Error, undefined]> {
	try {
		const data = await promise;
		return [undefined, data];
	} catch (error) {
		return [error as Error, undefined];
	}
}

export function tryCatch<T>(fn: () => T): [undefined, T] | [Error, undefined] {
	try {
		const data = fn();
		return [undefined, data];
	} catch (error) {
		return [error as Error, undefined];
	}
}

export const getFullName = (
	user:
		| {
				firstName?: string;
				lastName?: string;
				name?: string;
				email?: string;
		  }
		| undefined
) => {
	if (!user) return "Unknown";

	const { firstName, lastName, name, email } = user;

	if (firstName && lastName) {
		return `${firstName} ${lastName}`;
	}

	if (firstName) {
		return firstName;
	}

	if (lastName) {
		return lastName;
	}

	if (name) {
		return name;
	}

	if (email) {
		return email.split("@")[0] ?? "Unknown";
	}

	return "Unknown";
};

const KB = 1024;
export function formatBytes(bytes: number) {
	if (bytes === 0) return "0 B";
	const sizes = ["B", "KB", "MB", "GB", "TB"];
	const i = Math.floor(Math.log(bytes) / Math.log(KB));
	return `${(bytes / KB ** i).toFixed(i ? 1 : 0)} ${sizes[i]}`;
}

export function fileToAPI(value: string | File | undefined): File | undefined {
	if (!value) return null as unknown as undefined; // Deleted files ✓
	if (typeof value === "string") return undefined; // Unchanged files ✓
	if (value instanceof File) return value; // New files ✓
	return undefined;
}

export type SafeOmit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const syncLocales = (i18nLocale: i18n["language"]) => {
	const zodLocale = getZodLocale(i18nLocale);
	z.config(zodLocale());

	const dateFnsLocale = getDateFnsLocale(i18nLocale);
	setDefaultOptions({ locale: dateFnsLocale });
	pb.lang = i18nLocale;
};
